/**
 * integrityFormatters.js — human-language rendering for the
 * frozen six-state integrity vocabulary.
 *
 * Runtime evidence (Rev 4.4 Final E2E) showed the expanded detail
 * surface rendering raw enum tokens ("review_required",
 * "inspection_exception", "document_integrity_concern") directly to
 * the operator. Backend + API payloads + DB values remain the
 * frozen enums; this module is a UI-only presentation layer that
 * humanises them at render time. Never mutate the underlying value.
 *
 * Extend by adding to the map, not by branching in JSX.
 */

// Aggregate machine result — emp_candidate_integrity_checks.aggregate_status
const AGGREGATE_STATUS_HUMAN = {
  clear:              'No integrity indicators detected',
  review_required:    'Review recommended',
  unable_to_verify:   'Could not complete inspection',
};

// WP6 classifier — the six frozen dispatch-side states
const CLASSIFICATION_HUMAN = {
  clear:                 'Clear',
  included:              'Included after review',
  excluded:              'Excluded from evaluation',
  unresolved:            'Awaiting decision',
  grandfathered_legacy:  'Not checked (uploaded before this feature)',
  missing_check:         'Integrity check unavailable',
};

// Disposition — the human decision
const DISPOSITION_HUMAN = {
  included_for_evaluation:  'Included for evaluation',
  excluded_from_evaluation: 'Excluded from evaluation',
};

// Disposition reason — the enum authorised by Rev 4.4 §F.1 + Migration 151
const DISPOSITION_REASON_HUMAN = {
  document_integrity_concern: 'Document integrity concern',
  other:                      'Other (see note)',
};

// unable_to_verify failure codes
const FAILURE_CODE_HUMAN = {
  inspection_exception:            'The integrity inspection raised an unexpected error.',
  parser_metadata_unavailable:     'The parser produced text but per-glyph / per-run metadata was unavailable.',
  unsupported_document_structure:  'The document structure is not recognised by the current inspector.',
  parse_failed_upstream:           'The upstream parser could not extract text from this document.',
};

// Detector indicators
const INDICATOR_HUMAN = {
  white_text:          'White text',
  near_white_text:     'Near-white text',
  vanish:              'Hidden run (w:vanish)',
  web_hidden:          'Web-hidden element',
  tiny_font:           'Sub-visible font size',
  off_canvas:          'Off-canvas positioning',
  matrix_scaled:       'Matrix-scaled to invisible',
  low_contrast:        'Low contrast against background',
  zero_width_stuffing: 'Zero-width character stuffing',
};

const CONFIDENCE_HUMAN = {
  strong:    'Strong',
  heuristic: 'Heuristic',
};

// Titlecase fallback — used when we somehow receive a value not in
// the map (e.g. a future check_version adds a new enum). Never
// leaks the raw token because we always route through the map.
function _humanise(token) {
  if (!token || typeof token !== 'string') return '—';
  return token
    .split('_')
    .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : ''))
    .join(' ');
}

export function formatAggregateStatus(v)     { return AGGREGATE_STATUS_HUMAN[v]     || _humanise(v); }
export function formatClassification(v)      { return CLASSIFICATION_HUMAN[v]       || _humanise(v); }
export function formatDisposition(v)         { return DISPOSITION_HUMAN[v]          || _humanise(v); }
export function formatDispositionReason(v)   { return DISPOSITION_REASON_HUMAN[v]   || _humanise(v); }
export function formatFailureCode(v)         { return FAILURE_CODE_HUMAN[v]         || _humanise(v); }
export function formatIndicator(v)           { return INDICATOR_HUMAN[v]            || _humanise(v); }
export function formatConfidence(v)          { return CONFIDENCE_HUMAN[v]           || _humanise(v); }

// Reason options for the dispose dialog. Both are natural language;
// the raw enum token is carried only in the `value` field which is
// what the API expects.
export const REASON_OPTIONS = [
  { value: 'document_integrity_concern', label: 'Document integrity concern' },
  { value: 'other',                      label: 'Other (please explain in the note)' },
];
